import { ActivatedRouteSnapshot } from '@angular/router';

/**
 * Pure helpers for Google Analytics, kept apart from the service so they can be unit-tested.
 * The rule they enforce: nothing that identifies a record or a person leaves the browser in a
 * page path, title or referrer.
 */

/**
 * The pseudonymous ids Google Analytics knows a signed-in person by (never names or emails, which
 * Google's terms forbid). Must match AnalyticsUserRef.cs on the server, which turns the codes back
 * into names for Admin › Usage; both are tested against the same example.
 * - userId: GA's user_id, SHA-256 of "lrmidland:" + the internal user id, lowercase hex.
 * - userRef: the short code sent as the user_ref user property, readable in GA reports ("U-05B9D4F8").
 * Null where Web Crypto is unavailable (then nothing about the person is sent).
 */
export async function analyticsIdsFor(sub: string, subtle: SubtleCrypto | undefined): Promise<{ userId: string; userRef: string } | null> {
  if (!subtle || !sub) {
    return null;
  }
  const digest = await subtle.digest('SHA-256', new TextEncoder().encode(`lrmidland:${sub}`));
  const userId = Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('');
  return { userId, userRef: `U-${userId.slice(0, 8).toUpperCase()}` };
}

/** Hosts that never send analytics, whatever the server says: local runs and staging. */
export function isAnalyticsHost(hostname: string): boolean {
  const host = (hostname || '').toLowerCase();
  if (!host || host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host === '::1') {
    return false;
  }
  if (host.endsWith('.localhost') || host.endsWith('.local') || host.endsWith('.test')) {
    return false;
  }
  // Staging is lrmidland-stage.azurewebsites.net (the same check the nav bar's STAGING badge uses).
  return !host.includes('-stage') && !host.includes('staging');
}

/**
 * The page's route template, e.g. "/maintenance/vessels/ais-location-tracker/:id", built from the
 * matched route configuration so record ids (and any other route parameter) are never sent. Pages
 * in the same place group together in reports. Falls back to {@link maskPath} when nothing matched.
 */
export function routeTemplate(root: ActivatedRouteSnapshot, url: string): string {
  const parts: string[] = [];
  let node: ActivatedRouteSnapshot | null = root;
  let wildcard = false;
  while (node) {
    const path = node.routeConfig?.path;
    if (path === '**') {
      wildcard = true;
      break;
    }
    if (path) {
      parts.push(path);
    }
    node = node.firstChild;
  }
  if (wildcard || (parts.length === 0 && stripQuery(url) !== '/')) {
    return maskPath(url);
  }
  return '/' + parts.join('/');
}

/** Drops the query string and fragment (an OIDC callback carries ?code=…&state=…). */
export function stripQuery(url: string): string {
  const path = (url || '/').split(/[?#]/)[0];
  return path.startsWith('/') ? path : '/' + path;
}

/** Path with anything that looks like an id replaced by ":id" (numbers, GUIDs, long tokens, emails). */
export function maskPath(url: string): string {
  return stripQuery(url)
    .split('/')
    .map(segment => {
      const s = decodeSafe(segment);
      if (/^\d+$/.test(s)
        || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)
        || s.includes('@')
        || (s.length >= 16 && /\d/.test(s))) {
        return ':id';
      }
      return segment;
    })
    .join('/');
}

const WORDS: Record<string, string> = {
  ais: 'AIS', ai: 'AI', cp: 'CP', op: 'OP', mmsi: 'MMSI', lars: 'LARS', id: 'ID',
};

/** A readable page title from the template: "/maintenance/vessels/ais-location-tracker/:id" → "Maintenance › Vessels › AIS location tracker". */
export function titleFor(template: string): string {
  const names = template
    .split('/')
    .filter(s => s && !s.startsWith(':'))
    .map(segment => segment
      .split(/[-_]/)
      .filter(Boolean)
      .map((word, i) => WORDS[word.toLowerCase()] ?? (i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word.toLowerCase()))
      .join(' '));
  return names.length ? names.join(' › ') : 'Home';
}

/** The first page view's referrer: another site's origin only; our own pages as a masked path. */
export function initialReferrer(referrer: string, origin: string): string {
  if (!referrer) {
    return '';
  }
  try {
    const ref = new URL(referrer);
    return ref.origin === origin ? origin + maskPath(ref.pathname) : ref.origin + '/';
  } catch {
    return '';
  }
}

/** "/api/ExportReportInvoices/2026?x=1" → "ExportReportInvoices". */
export function apiName(url: string): string | null {
  const match = /\/api\/([A-Za-z][A-Za-z0-9]*)/.exec(url || '');
  return match ? match[1] : null;
}

/** "attachment; filename=\"Invoices 2026.xlsx\"" → "xlsx". */
export function fileExtension(contentDisposition: string | null, contentType: string | null): string | null {
  const name = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition || '')?.[1];
  const ext = name ? /\.([A-Za-z0-9]{1,8})$/.exec(decodeSafe(name.trim()))?.[1] : null;
  if (ext) {
    return ext.toLowerCase();
  }
  const type = (contentType || '').toLowerCase();
  if (type.includes('spreadsheetml')) { return 'xlsx'; }
  if (type.includes('wordprocessingml')) { return 'docx'; }
  if (type.includes('pdf')) { return 'pdf'; }
  if (type.includes('csv')) { return 'csv'; }
  return null;
}

function decodeSafe(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
